import { useState } from "react";
import ScrollReveal from "@/components/base/ScrollReveal";

const faqs = [
  {
    category: "Insurance",
    q: "Do you accept insurance?",
    a: "Perspective Therapy is a self-pay and out-of-network practice. We offer courtesy billing for standard sessions, which means we bill you directly and submit claims to your insurance for potential reimbursement."
  },
  {
    category: "Insurance",
    q: "How long are therapy sessions?",
    a: "Standard sessions are 45-50 minutes. Clients may request a double session when clinically appropriate. If using out-of-network benefits, insurance typically does not reimburse for sessions longer than 60 minutes on the same day."
  },
  {
    category: "Cost",
    q: "Do you offer therapy packages?",
    a: "Therapy packages are available for new clients in 4, 8, and 12-session options. Packages are self-pay only and are not eligible for insurance billing."
  },
  {
    category: "Cost",
    q: "What does therapy cost at Perspective?",
    a: "Individual therapy ranges from $65-$200 per session. Couples therapy is $200 per session. Therapist-specific rates are listed on profile pages."
  },
  {
    category: "Cost",
    q: "Is there a fee for the initial consultation?",
    a: "No. Your first 15-minute consultation call is free. It's a chance to ask questions, get a sense of the therapist, and decide whether the fit feels right before starting.",
  },
  {
    category: "Therapist Selection",
    q: "How do I choose the right therapist?",
    a: "You can browse therapist profiles and book directly with someone whose background and approach feel like a fit. If you'd prefer a recommendation, fill out a short form and we'll suggest the best match based on what you're working through, your availability, and your preferences.",
  },
  {
    category: "Therapist Selection",
    q: "What if I want to switch therapists?",
    a: "That's completely fine and more common than most people expect. Just let us know and we'll help you transition to a different therapist without any awkwardness.",
  },
  {
    category: "Telehealth",
    q: "How does telehealth work?",
    a: "Telehealth sessions run through a secure, HIPAA-compliant video platform. You'll receive a session link in our courtesy reminders. All you need is a private space, a stable internet connection, and a device with video for telehealth. We offer telehealth to anyone in New York, New Jersey, or Indiana.",
  },
  {
    category: "Telehealth",
    q: "Is telehealth as effective as in-person therapy?",
    a: "Research consistently shows telehealth is equally effective for most concerns, including anxiety, depression, and relationship issues. A lot of clients actually prefer it for the convenience and privacy. If you'd rather meet in person, we have an office in Larchmont.",
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="faq" className="w-full scroll-mt-[108px] py-14 md:scroll-mt-[112px] md:py-20" style={{ background: "linear-gradient(180deg, #FBF9F6 0%, #F8F4EE 100%)" }}>
      <div className="w-full px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-12 xl:gap-20">
          {/* Left */}
          <div className="h-fit xl:sticky xl:top-28">
            <ScrollReveal variant="fadeIn" delay={0}>
              <span className="font-manrope text-[13px] font-semibold tracking-widest uppercase block mb-4" style={{ color: "#6B742F" }}>
                Common Questions
              </span>
            </ScrollReveal>
            <ScrollReveal variant="fadeUp" delay={80}>
              <h2 className="font-fraunces text-[#2B2B2B] text-4xl md:text-5xl font-light leading-snug mb-6">
                Everything you<br />
                <em className="not-italic" style={{ color: "#7D54FC" }}>need to know</em>
              </h2>
            </ScrollReveal>
            <ScrollReveal variant="fadeUp" delay={160}>
              <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed mb-8">
                Still have questions? Feel free to reach out directly before you commit.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 border border-[#2B2B2B]/20 text-[#2B2B2B] hover:bg-[#2B2B2B] hover:text-white font-manrope text-[15px] font-semibold px-6 py-3.5 rounded-full transition-all cursor-pointer whitespace-nowrap btn-outline"
              >
                Ask us directly <i className="ri-arrow-right-line"></i>
              </a>
            </ScrollReveal>

          </div>

          {/* Right: Accordion */}
          <div className="flex flex-col divide-y divide-[#CBBFAF]/35">
            {faqs.map((faq, i) => (
              <ScrollReveal key={faq.q} variant="fadeUp" delay={i * 55} threshold={0.05}>
                <div className="py-5 faq-row px-3 -mx-3">
                  <button
                    onClick={() => setOpenIndex(openIndex === i ? null : i)}
                    className="w-full flex items-start justify-between gap-4 text-left cursor-pointer group"
                  >
                    <div>
                      <span className="font-fraunces text-[#2B2B2B] text-[18px] font-medium leading-snug group-hover:text-[#3C6FF8] transition-colors">
                        {faq.q}
                      </span>
                    </div>
                    <span className="w-6 h-6 flex items-center justify-center shrink-0 mt-4">
                      <i className={`text-[#2B2B2B]/40 text-base transition-transform duration-200 ${openIndex === i ? "ri-subtract-line" : "ri-add-line"}`}></i>
                    </span>
                  </button>
                  {openIndex === i && (
                    <p className="font-manrope text-[#2B2B2B]/82 text-[15px] leading-relaxed mt-4 pr-10">
                      {faq.a}
                    </p>
                  )}
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
